import {
  closestCorners,
  CollisionDetection,
  DndContextProps,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  getFirstCollision,
  PointerSensor,
  pointerWithin,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { type LanguageCode } from '@sparted/shared-library/business/types';
import React, { useCallback, useMemo, useState } from 'react';

import SContentCard from 'Components/structural/SContentCard/SContentCard';
import { formatTranslationStatuses } from 'Pages/Content/ContentList/utils';
import { type PartialContentForCampaignForm } from 'Store/entities/activity/activity.type';
import { DrawerContent } from 'Store/entities/content/content-drawer/content-drawer.types';

export const DROP_AREA = 'drop-area';

/**
 * This offset is needed to avoid id conflicts when dragging items.
 * Sessions container ids are generated by adding this offset to the session index otherwise multiple items can share the same id
 * for the first few contents with ids 1-10 approximately.
 */
export const SESSION_ID_OFFSET = 1_000_000;

type Sessions = PartialContentForCampaignForm[][];

export type UseContentsDragAndDrop = (params: {
  selectedLanguage: LanguageCode;
  sessions: Sessions;
  onSessionChange: (newSessions: Sessions) => void;
}) => {
  overlayItem: JSX.Element | undefined;
  contextProps: Partial<DndContextProps>;
};

const getSessionContainerIndex = (id: UniqueIdentifier) => Number(id) - SESSION_ID_OFFSET;

const customCollisionDetection: CollisionDetection = (args) => {
  const pointerIntersections = pointerWithin(args);

  const intersections =
    pointerIntersections.length > 0
      ? // If there are droppables intersecting with the pointer, return those
        pointerIntersections
      : closestCorners(args);

  const over = getFirstCollision(intersections);

  return over ? [over] : [];
};

export const useContentsDragAndDrop: UseContentsDragAndDrop = ({ sessions, selectedLanguage, onSessionChange }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  const [draggedItem, setDraggedItem] = useState<PartialContentForCampaignForm | undefined>(undefined);

  const overlayItem = useMemo(
    () =>
      draggedItem && (
        <SContentCard
          key={`dragged-${draggedItem.id}`}
          id={`#${draggedItem.identifier || draggedItem.id}`}
          canShowOverlay={true}
          selectable={false}
          previewable={false}
          title={draggedItem.name?.[selectedLanguage] || ''}
          type={draggedItem.gameType}
          translationStatuses={formatTranslationStatuses(draggedItem.statuses, draggedItem.contentId)}
          imageUrl={draggedItem.coverImage?.url || ''}
        />
      ),
    [draggedItem, selectedLanguage],
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      setDraggedItem(undefined);

      if (!active || !over) return;

      const activeItem: PartialContentForCampaignForm | DrawerContent = active.data.current?.content;
      const sourceSessionIndex = sessions.findIndex((session) => session.some(({ id }) => id === activeItem.id));
      const candidateOverSessionIndex = sessions.findIndex((session) => session.some(({ id }) => id === over.id));
      const overSessionIndex =
        candidateOverSessionIndex === -1 ? getSessionContainerIndex(over.id) : candidateOverSessionIndex;

      // HandleDragOver should handle movement across sessions and from the drawer
      if (sourceSessionIndex !== overSessionIndex) return;

      const activeItemIndex = sessions[sourceSessionIndex]?.findIndex(({ id }) => id === activeItem.id);
      const overItemIndex = sessions[overSessionIndex]?.findIndex(({ id }) => id === over.id);

      const newActiveItem: PartialContentForCampaignForm =
        'uiId' in activeItem
          ? {
              id: activeItem.uiId,
              contentId: activeItem.id,
              name: activeItem.title,
              identifier: activeItem.customId,
              statuses: activeItem.statuses,
              coverImage: { url: activeItem.coverUrl },
              gameType: activeItem.gameType,
              archived: activeItem.archived,
            }
          : activeItem;

      const currentSession = [...sessions[sourceSessionIndex]];
      currentSession.splice(activeItemIndex, 1);
      currentSession.splice(overItemIndex, 0, newActiveItem);

      onSessionChange(sessions.map((session, index) => (index === sourceSessionIndex ? currentSession : session)));
    },
    [sessions],
  );

  const handleDragOver = useCallback(
    ({ active, over, collisions, activatorEvent }: DragOverEvent) => {
      if (typeof over?.id !== 'number') return;

      const activeItem = active.data.current?.content as PartialContentForCampaignForm;

      const sourceSessionIndex = sessions.findIndex((session) => session.some(({ id }) => id === activeItem.id));
      const candidateOverSessionIndex = sessions.findIndex((session) => session.some(({ id }) => id === over.id));

      const targetSessionIndex =
        candidateOverSessionIndex === -1 && over.data.current?.type === DROP_AREA
          ? getSessionContainerIndex(over.id)
          : candidateOverSessionIndex;

      const newSessions = sessions.map((session, index) => {
        if (index === sourceSessionIndex) {
          return sessions[sourceSessionIndex].filter(({ id }) => id !== activeItem.id);
        }

        if (index === targetSessionIndex) {
          const hoveredItemIndex = session.findIndex(({ id }) => id === over.id);
          if (hoveredItemIndex !== -1) {
            // Determine whether to insert activeItem to the left or right of the hovered item
            const pointerX = (activatorEvent as PointerEvent).clientX;
            const hoveredItemRect = collisions?.[0]?.data?.droppableContainer.rect;
            const insertIndex = pointerX < hoveredItemRect.left ? hoveredItemIndex : hoveredItemIndex + 1;

            return [...session.slice(0, insertIndex), activeItem, ...session.slice(insertIndex)];
          }

          // If the hovered item is not found, simply append the activeItem
          return [...session, activeItem];
        }

        return session;
      });

      if (targetSessionIndex === -1) return;

      // Avoid movement in the same session, let handleDragEnd handle that
      if (targetSessionIndex === sourceSessionIndex) return;

      return onSessionChange(newSessions);
    },
    [sessions],
  );

  const handleDragStart = useCallback(({ active }: DragStartEvent) => {
    const content: PartialContentForCampaignForm | DrawerContent = active.data.current?.content;

    const draggedItem =
      'uiId' in content
        ? {
            id: content.uiId,
            contentId: content.id,
            name: content.title,
            identifier: content.customId,
            statuses: content.statuses,
            coverImage: { url: content.coverUrl },
            gameType: content.gameType,
            archived: content.archived,
          }
        : content;

    setDraggedItem(draggedItem);
  }, []);

  return {
    overlayItem,
    contextProps: {
      sensors,
      autoScroll: false,
      onDragEnd: handleDragEnd,
      onDragMove: handleDragOver,
      onDragStart: handleDragStart,
      collisionDetection: customCollisionDetection,
    },
  };
};
